import React from 'react';
import styles from './PatientInfo.module.css';
import ViewMoreIcon from "@mui/icons-material/ContactPageOutlined";
import PatientConfig from '../../../../consts/PatientConfig';
import CustomButton from '../../../../components/buttons/CustomButton';
import { getPatientAvatar, hasDrawableGender } from '../../../../utils/patientAvatarUtils';
import CalendarConfig from '../../../../consts/CalendarConfig';
import { formatPhone } from '../../../../utils/phoneUtils';
import { GetFriendlyCPF } from '../../../../utils/Utils';
import { getInitials } from '../../../appointments/v2/utils/appointmentUtils';
import StarIcon from '@mui/icons-material/AutoAwesome';
import WarningIcon from '@mui/icons-material/WarningAmberRounded';

const NOT_INFORMED = "Não informado";

/**
 * Card do paciente no cabeçalho do atendimento: avatar, nome, selo de primeiro
 * ou enésimo atendimento, dados de identificação com rótulo (nascimento, idade,
 * gênero, telefone, último atendimento e responsável quando houver) e atalho
 * para a ficha.
 *
 * `visits`: { previousCount, lastDate } com os atendimentos concluídos antes
 * deste; ausente enquanto a página carrega.
 */
const PatientInfo = ({ data, visits }) => {
  // Safe access to data with default values
  const safeData = {
    gender: data?.gender || "99",
    birthday: data?.birthday || null,
    name: data?.name || "Nome não informado",
    cpf: data?.cpf || "",
    id: data?.id || "",
    phone: data?.phone || "",
    responsibleName: data?.financial_responsible_name || "",
    responsibleRelationship: data?.financial_responsible_relationship || "",
  };

  // Safe access to gender with fallback
  const genderInfo = PatientConfig.GetGenderById(safeData.gender);
  const genderLabel = genderInfo?.Label || NOT_INFORMED;

  // Sem gênero informado não há desenho certo: mostra as iniciais, como a agenda
  const hasGender = hasDrawableGender(safeData.gender);
  const getAvatar = () => getPatientAvatar(safeData.gender, safeData.birthday);

  function OnPatientInfoClickHandler() {
    if (safeData.id) {
      window.open(`/patients/${safeData.id}`);
    }
  }

  const responsibleLabel = safeData.responsibleName
    ? safeData.responsibleName + (safeData.responsibleRelationship ? ` (${safeData.responsibleRelationship})` : "")
    : "";

  const previousCount = visits ? Number(visits.previousCount) || 0 : null;
  const isFirstVisit = previousCount === 0;
  const visitLabel = isFirstVisit ? "Primeiro atendimento" : `${previousCount + 1}º atendimento`;

  const hasCpf = safeData.cpf.trim() !== '';

  const details = [
    { label: "Nascimento", value: safeData.birthday ? CalendarConfig.GetFullDateName(safeData.birthday) : NOT_INFORMED },
    { label: "Idade", value: safeData.birthday ? CalendarConfig.GetAge(safeData.birthday) : "Não informada" },
    { label: "Gênero", value: genderLabel },
    { label: "Telefone", value: safeData.phone ? formatPhone(safeData.phone) : NOT_INFORMED },
    // CPF em falta é um alerta, não um dado vazio: fica em destaque para ser corrigido na ficha
    hasCpf
      ? { label: "CPF", value: GetFriendlyCPF(safeData.cpf) || safeData.cpf }
      : { label: "CPF", value: "Não cadastrado", warning: true },
  ];

  if (visits?.lastDate) {
    details.push({ label: "Último atendimento", value: CalendarConfig.GetFullDateName(CalendarConfig.GetDDMMAAAA(visits.lastDate)) });
  }

  if (responsibleLabel) {
    details.push({ label: "Responsável", value: responsibleLabel, wide: true });
  }

  return (
    <div className={styles.container}>
      <div className={styles.identity}>
        {hasGender ? (
          <div className={styles.imageContainer}>
            <img src={getAvatar()} alt="" width={"100%"} height={"100%"} />
          </div>
        ) : (
          <div className={`${styles.imageContainer} ${styles.initials}`} aria-hidden="true">
            {getInitials(data?.name)}
          </div>
        )}
        <div className={styles.texts}>
          <span className={styles.eyebrow}>Paciente</span>
          <div className={styles.nameRow}>
            <span className={styles.name}>{safeData.name}</span>
          </div>
          {previousCount !== null && (
            <span className={`${styles.visitPill} ${isFirstVisit ? styles.visitPillFirst : ''}`}>
              {isFirstVisit && <StarIcon />}
              {visitLabel}
            </span>
          )}
        </div>

        <div className={styles.identityAction}>
          <CustomButton
            label="Ver ficha"
            style="primary"
            variant="outlined"
            onClick={OnPatientInfoClickHandler}
            icon={<ViewMoreIcon />}
            enabled={!!safeData.id}
          />
        </div>
      </div>

      <div className={styles.details}>
        {details.map((item) => (
          <div key={item.label} className={`${styles.detail} ${item.wide ? styles.detailWide : ''}`}>
            <span className={styles.detailLabel}>{item.label}</span>
            <span className={`${styles.detailValue} ${item.value === NOT_INFORMED || item.value === "Não informada" ? styles.detailEmpty : ''} ${item.warning ? styles.detailWarning : ''}`}>
              {item.warning && <WarningIcon />}
              {item.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PatientInfo;
